import {ExportOutlined} from '@ant-design/icons';
import {LoadingOutlined} from '@ant-design/icons/lib';
import {Button, Modal, notification, Typography} from 'antd';
import {
  ChargeStatus,
  ExportChargesMutationVariables,
  ExportStatus,
  SearchableChargeFilterInput,
  useDownloadExportMutation,
  useStartExportChargesMutation
} from 'api/generated';
import {Formik} from 'formik';
import {Checkbox, DatePicker, Form, FormItem, Radio, Select, Transfer} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {useCheckExportStatus} from 'hooks/useCheckExportStatus';
import {useUserContext} from 'hooks/useUserContext';
import {downloadFile, omit} from 'lib/utils';
import moment, {Moment} from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {chargeColumns, defaultChargeColumns} from './types';

type Props = {
  filter: SearchableChargeFilterInput;
};

const RadioBlock = styled(Radio)`
  display: block !important;
  line-height: 34px !important;
  height: 34px !important;
`;

const {Option} = Select;

const RangePicker = styled(DatePicker.RangePicker)`
  width: 280px;
`;

const Label = styled.span`
  display: inline-block;
  width: 150px;
`;

const {Text} = Typography;

const columns = chargeColumns.map((key) => ({key, title: key}));

const timezones = moment.tz.names();

enum filterType {
  custom,
  today,
  currentMonth,
  lastWeek,
  lastMonth
}

enum columnsPreset {
  default,
  custom
}

type FormValues = {
  filter: filterType;
  timezone: string;
  includeColumns: columnsPreset;
  columns: string[];
  statuses: string[];
  createdAt: Moment[];
};

const getSelectedRange = (filter: SearchableChargeFilterInput) => {
  const [_start, _end] = filter.createdAt!.range!;
  const start = moment.unix(_start!);
  const end = moment.unix(_end!);
  return [start, end];
};

const getCustomRangeLabel = (createAt: Moment[]) => {
  const [start, end] = createAt;
  if (start.isSame(end, 'day')) return start.format('ll');
  return `${start.format('ll')} - ${end.format('ll')}`;
};

const buildFilter = (values: FormValues, props: Props) => {
  const timezone = values.timezone;
  let filter: SearchableChargeFilterInput = omit(props.filter, ['status']);
  if (values.statuses.length === 1) {
    const status = values.statuses[0];
    filter.status = {match: status};
  } else {
    filter.or = values.statuses.map((status) => ({status: {match: status}}));
  }
  if (values.filter === filterType.custom) {
    const [start, end] = values.createdAt;
    filter.createdAt = {
      range: [
        start.startOf('day').tz(timezone).add(12, 'hours').startOf('day').unix(),
        end.endOf('day').tz(timezone).subtract(12, 'hours').endOf('day').unix()
      ]
    };
  }
  if (values.filter === filterType.today) {
    filter.createdAt = {
      range: [
        moment().tz(timezone).startOf('day').unix(),
        moment().tz(timezone).endOf('day').unix()
      ]
    };
  }
  if (values.filter === filterType.currentMonth) {
    filter.createdAt = {
      range: [
        moment().tz(timezone).startOf('month').unix(),
        moment().tz(timezone).endOf('day').unix()
      ]
    };
  }
  if (values.filter === filterType.lastWeek) {
    filter.createdAt = {
      range: [
        moment().tz(timezone).startOf('day').subtract(7, 'days').unix(),
        moment().tz(timezone).endOf('day').unix()
      ]
    };
  }
  if (values.filter === filterType.lastMonth) {
    filter.createdAt = {
      range: [
        moment().tz(timezone).startOf('day').subtract(1, 'month').unix(),
        moment().tz(timezone).endOf('day').unix()
      ]
    };
  }
  return filter;
};

const getExportErrorDescription = (statusCode = 'F999') => {
  switch (statusCode) {
    case 'F001':
      return "We couldn't find any data to export based on your selections. Please adjust your filters and try again.";
    default:
      return 'An error occurred while exporting the payments. Please try again.';
  }
};

export const ExportPayments: React.FC<Props> = (props) => {
  const [isOpen, setOpen] = useState(false);

  const [exportId, setExportId] = useState(null as any);
  const user = useUserContext();

  const [startExportCharges] = useStartExportChargesMutation();
  const [downloadExport] = useDownloadExportMutation();
  const {data: exportStatusData, loading, error} = useCheckExportStatus(exportId);
  const [isDownloading, setIsDownloading] = useState(false);

  const statuses = [
    {
      value: ChargeStatus.SUCCEEDED,
      label: 'Succeeded'
    },
    {
      value: ChargeStatus.REFUNDED,
      label: 'Refunded'
    },
    {
      value: ChargeStatus.PARTIALLY_REFUNDED,
      label: 'Partially refunded'
    },
    {
      value: ChargeStatus.FAILED,
      label: 'Failed'
    },
    {
      value: ChargeStatus.CANCELED,
      label: 'Canceled'
    },
    {
      value: ChargeStatus.PAID_OUT,
      label: 'Paid out'
    }
  ];

  const initialValues: FormValues = {
    filter: filterType.today,
    timezone: moment.tz.guess(),
    includeColumns: columnsPreset.default,
    columns: defaultChargeColumns,
    statuses: [ChargeStatus.SUCCEEDED, ChargeStatus.REFUNDED, ChargeStatus.PARTIALLY_REFUNDED],
    createdAt: getSelectedRange(props.filter)
  };

  const handleExport = async (
    values: FormValues,
    {setSubmitting, resetForm}: FormikHelpers<FormValues>
  ) => {
    const {timezone, columns, includeColumns} = values;
    const filter = buildFilter(values, props);
    const variables: ExportChargesMutationVariables = {filter, timezone};
    if (includeColumns === columnsPreset.custom && columns.length > 0) {
      variables.columns = columns;
    }

    const {data} = await startExportCharges({
      variables
    });

    const newExportId = data?.startExportCharges?.id;

    if (!newExportId) {
      notification.error({
        key: 'export_job',
        message: 'Export failed',
        description: getExportErrorDescription()
      });
    } else {
      startPollingExportStatus(newExportId);
    }

    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  const startPollingExportStatus = (newExportId: string) => {
    notification.info({
      key: 'export_job',
      duration: 0,
      closeIcon: <div />,
      icon: <LoadingOutlined />,
      message: 'Export is in progress',
      description: 'This process might take a few seconds'
    });
    setExportId(newExportId);
    setIsDownloading(false);
  };

  const downloadExportFile = async () => {
    setIsDownloading(true);
    const downloadUrl = await downloadExport({
      variables: {
        id: exportId,
        accountId: user.userId
      }
    });
    downloadFile(downloadUrl.data!.downloadExport.downloadUrl);
    notification.close('export_job');
  };

  useEffect(() => {
    if (error) {
      notification.error({
        key: 'export_job',
        message: 'Export failed',
        description: getExportErrorDescription()
      });
    } else if (exportStatusData) {
      const status = exportStatusData?.chargesExport?.status;
      if (status === ExportStatus.COMPLETED) {
        notification.success({
          key: 'export_job',
          duration: 0,
          message: 'Export completed',
          description: 'Download the export file by clicking the button below',
          btn: (
            <Button type="primary" onClick={downloadExportFile} loading={isDownloading}>
              Download
            </Button>
          )
        });
      } else if (status === ExportStatus.FAILED) {
        notification.error({
          key: 'export_job',
          duration: 5,
          message: 'Export payments failed',
          description: getExportErrorDescription(
            exportStatusData?.chargesExport?.statusCode || 'F999'
          )
        });
      }
    }
  }, [error, exportStatusData, isDownloading]);

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <ExportOutlined /> Export
      </Button>
      <Formik initialValues={initialValues} onSubmit={handleExport} enableReinitialize={!isOpen}>
        {({submitForm, isSubmitting, values, resetForm}) => (
          <Modal
            width={650}
            title="Export payments"
            okText="Export"
            cancelText="Cancel"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            onCancel={() => {
              setOpen(false);
              setTimeout(resetForm, 100);
            }}>
            <Form layout="vertical">
              <FormItem
                name="timezone"
                label="Timezone"
                extra={
                  <>
                    All the dates will be formatted in the selected timezone. <br />
                    Payments are exported from 12:00 AM to 11:59 PM in the selected timezone.
                  </>
                }>
                <Select name="timezone" showSearch style={{maxWidth: 300}}>
                  {timezones.map((tz) => (
                    <Option key={tz} value={tz}>
                      {tz}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem name="filter" label="Filters">
                <Radio.Group name="filter">
                  <RadioBlock value={filterType.today} name="filter">
                    <Label>Today</Label>
                    <Text type="secondary">{moment().tz(values.timezone).format('ll')}</Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.currentMonth} name="filter">
                    <Label>Current month</Label>
                    <Text type="secondary">
                      {moment().tz(values.timezone).startOf('month').format('ll')} -{' '}
                      {moment().tz(values.timezone).format('ll')}
                    </Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.lastWeek} name="filter">
                    <Label>Last 7 days</Label>
                    <Text type="secondary">
                      {moment().tz(values.timezone).startOf('day').subtract(7, 'days').format('ll')}{' '}
                      - {moment().tz(values.timezone).format('ll')}
                    </Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.lastMonth} name="filter">
                    <Label>Last month</Label>
                    <Text type="secondary">
                      {moment()
                        .tz(values.timezone)
                        .startOf('day')
                        .subtract(1, 'month')
                        .format('ll')}{' '}
                      - {moment().tz(values.timezone).format('ll')}
                    </Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.custom} name="filter">
                    <Label>Custom</Label>
                    {values.filter === filterType.custom ? (
                      <RangePicker
                        name="createdAt"
                        size="small"
                        format="ll"
                        allowClear={false}
                        disabledDate={(date) =>
                          date ? date.isAfter(moment().endOf('day')) : false
                        }
                      />
                    ) : (
                      <Text type="secondary">{getCustomRangeLabel(values.createdAt)}</Text>
                    )}
                  </RadioBlock>
                </Radio.Group>
              </FormItem>
              <FormItem name="statuses" label="Payments">
                <Checkbox.Group name="statuses" options={statuses} />
              </FormItem>
              <FormItem name="includeColumns" label="Columns">
                <Radio.Group name="includeColumns">
                  <Radio value={columnsPreset.default} name="includeColumns">
                    Default columns
                  </Radio>
                  <Radio value={columnsPreset.custom} name="includeColumns">
                    Custom columns
                  </Radio>
                </Radio.Group>
              </FormItem>
              {values.includeColumns === columnsPreset.custom && (
                <FormItem name="columns" label="Select columns">
                  <Transfer
                    showSearch
                    name="columns"
                    titles={['Not included', 'Included']}
                    dataSource={columns}
                    listStyle={{width: 280, height: 350}}
                    render={(item: any) => item.title}
                  />
                </FormItem>
              )}
              {values.includeColumns === columnsPreset.default && (
                <FormItem name="columns" label="Included columns">
                  <Text type="secondary">{defaultChargeColumns.join(', ')}</Text>
                </FormItem>
              )}
              <input type="submit" value="submit" hidden />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
